// Generate calendar links/files for bookings

interface CalendarEvent {
  title: string;
  description: string;
  location: string;
  startDate: Date;
  durationMinutes: number;
}

function formatDateForICS(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function formatDateForGoogle(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function parseDurationMinutes(duration: string): number {
  if (!duration) return 60;
  const lower = duration.toLowerCase();
  let total = 0;
  const hrMatches = lower.matchAll(/(\d+(?:\.\d+)?)\s*(?:h|hr|hour)/g);
  for (const match of hrMatches) total += Math.round(parseFloat(match[1]) * 60);
  const minMatches = lower.matchAll(/(\d+)\s*(?:m|min|minute)/g);
  for (const match of minMatches) total += parseInt(match[1], 10);
  if (total === 0) {
    const bare = lower.match(/^\s*(\d+)\s*$/);
    if (bare) total = parseInt(bare[1], 10);
  }
  return total || 60;
}

function parseBookingTime(dateStr: string, timeStr: string): Date {
  const date = new Date(dateStr + "T00:00:00");
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (match) {
    let hours = parseInt(match[1]);
    const mins = parseInt(match[2]);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    date.setHours(hours, mins, 0, 0);
  }
  return date;
}

export function createCalendarEvent(booking: {
  service_name: string;
  service_category: string;
  booking_date: string;
  booking_time: string;
  duration: string;
}): CalendarEvent {
  const startDate = parseBookingTime(booking.booking_date, booking.booking_time);
  const durationMinutes = parseDurationMinutes(booking.duration);
  return {
    title: `${booking.service_name} – In Paradise Beauty`,
    description: `${booking.service_category}: ${booking.service_name}\nDuration: ${booking.duration}\n\nRemember to arrive on time and come with bare nails.`,
    location: "In Paradise Beauty",
    startDate,
    durationMinutes,
  };
}

export function generateGoogleCalendarUrl(event: CalendarEvent): string {
  const endDate = new Date(event.startDate.getTime() + event.durationMinutes * 60000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${formatDateForGoogle(event.startDate)}/${formatDateForGoogle(endDate)}`,
    details: event.description,
    location: event.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function generateOutlookCalendarUrl(event: CalendarEvent): string {
  const endDate = new Date(event.startDate.getTime() + event.durationMinutes * 60000);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: event.startDate.toISOString(),
    enddt: endDate.toISOString(),
    body: event.description,
    location: event.location,
  });
  return `https://outlook.live.com/calendar/0/action/compose?${params.toString()}`;
}

export function generateICSFile(event: CalendarEvent): string {
  const endDate = new Date(event.startDate.getTime() + event.durationMinutes * 60000);
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//In Paradise Beauty//Booking//EN",
    "BEGIN:VEVENT",
    `DTSTART:${formatDateForICS(event.startDate)}`,
    `DTEND:${formatDateForICS(endDate)}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description.replace(/\n/g, "\\n")}`,
    `LOCATION:${event.location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadICS(event: CalendarEvent) {
  const ics = generateICSFile(event);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "appointment.ics";
  a.click();
  URL.revokeObjectURL(url);
}

interface BookingForICS {
  id: string;
  service_name: string;
  service_category: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  status?: string;
  notes?: string | null;
}

function escapeICS(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

/**
 * Build a multi-event ICS file for an entire client's booking history.
 * Cancelled bookings are still included with STATUS:CANCELLED so calendars can
 * surface or hide them appropriately.
 */
export function generateClientICS(
  clientName: string,
  bookings: BookingForICS[],
): string {
  const dtstamp = formatDateForICS(new Date());
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//In Paradise Beauty//Client Bookings//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${escapeICS(`${clientName} – In Paradise Beauty`)}`,
  ];

  for (const b of bookings) {
    const event = createCalendarEvent(b);
    const endDate = new Date(event.startDate.getTime() + event.durationMinutes * 60000);
    const status = (b.status || "confirmed").toLowerCase();
    const icsStatus = status === "cancelled" ? "CANCELLED" : "CONFIRMED";
    const description = `${event.description}${b.notes ? `\n\nNotes: ${b.notes}` : ""}`;

    lines.push(
      "BEGIN:VEVENT",
      `UID:${b.id}@inparadisebeauty.com`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${formatDateForICS(event.startDate)}`,
      `DTEND:${formatDateForICS(endDate)}`,
      `SUMMARY:${escapeICS(`${clientName} – ${b.service_name}`)}`,
      `DESCRIPTION:${escapeICS(description)}`,
      `LOCATION:${escapeICS(event.location)}`,
      `STATUS:${icsStatus}`,
      "END:VEVENT",
    );
  }

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

export function downloadClientICS(clientName: string, bookings: BookingForICS[]) {
  const ics = generateClientICS(clientName, bookings);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = clientName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "client";
  a.href = url;
  a.download = `${safeName}-bookings.ics`;
  a.click();
  URL.revokeObjectURL(url);
}
